import React, { useState, useEffect } from 'react';
import './DashboardMessagesBlock.css';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const timeOptions = ['Today', 'This Week', 'This Month', 'This Year', 'All Time'];

const DashboardMessagesBlock = ({ data, currentUser }) => {
  const [showChart, setShowChart] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('This Month');
  const [groupByUser, setGroupByUser] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  const { summary = {}, messages = [] } = data || {};
  const {
    total = 0,
    incoming = 0,
    outbound = 0,
    sent = 0,
    delivered = 0,
    read = 0,
    failed = 0,
  } = summary;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.post("https://gambot.azurewebsites.net/api/Webhooks/GetAllUsersByOrg", {
          organization: currentUser?.organization,
        });
        setUsers(response.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (currentUser?.organization) fetchUsers();
  }, [currentUser?.organization]);

  const filterMessagesByTime = (range) => {
    if (range === 'All Time') return messages;

    const now = new Date();
    return messages.filter((msg) => {
      const created = new Date(msg.createdOn);
      const diffHours = (now - created) / (1000 * 60 * 60);

      switch (range) {
        case 'Today':
          return created.toDateString() === now.toDateString();
        case 'This Week':
          return diffHours < 168;
        case 'This Month':
          return now.getMonth() === created.getMonth() && now.getFullYear() === created.getFullYear();
        case 'This Year':
          return now.getFullYear() === created.getFullYear();
        default:
          return true;
      }
    });
  };

  const filteredMessages = filterMessagesByTime(selectedTimeRange);

  const chartData = (() => {
    if (groupByUser) {
      const userMap = {};
      filteredMessages.forEach((msg) => {
        if (!msg.sentBy || msg.direction !== 'outbound') return;
        userMap[msg.sentBy] = (userMap[msg.sentBy] || 0) + 1;
      });

      return {
        labels: Object.keys(userMap),
        datasets: [
          {
            label: 'Outbound by User',
            data: Object.values(userMap),
            backgroundColor: '#2e6155',
            borderRadius: 8,
          },
        ],
      };
    }

    if (selectedUser) {
      const filtered = filteredMessages.filter(
        (msg) => msg.sentBy === selectedUser && msg.direction === 'outbound'
      );

      const countByStatus = {
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
      };

      filtered.forEach((msg) => {
        const status = msg.status?.toLowerCase();
        if (status === 'sent') countByStatus.sent++;
        else if (status === 'delivered') countByStatus.delivered++;
        else if (status === 'read') countByStatus.read++;
        else if (['fail', 'failed', 'error'].includes(status)) countByStatus.failed++;
      });

      return {
        labels: ['Sent', 'Delivered', 'Read', 'Failed'],
        datasets: [
          {
            label: `Status for ${selectedUser}`,
            data: [
              countByStatus.sent,
              countByStatus.delivered,
              countByStatus.read,
              countByStatus.failed,
            ],
            backgroundColor: ['#4CAF50', '#2196F3', '#FFC107', '#F44336'],
            borderRadius: 8,
          },
        ],
      };
    }

    // Recalculate from filtered messages
    const stats = {
      total: 0,
      incoming: 0,
      outbound: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
    };

    filteredMessages.forEach((msg) => {
      stats.total++;
      if (msg.direction === 'incoming') stats.incoming++;
      if (msg.direction === 'outbound') stats.outbound++;

      const status = msg.status?.toLowerCase();
      if (status === 'sent') stats.sent++;
      else if (status === 'delivered') stats.delivered++;
      else if (status === 'read') stats.read++;
      else if (['fail', 'failed', 'error'].includes(status)) stats.failed++;
    });

    return {
      labels: ['Total', 'Incoming', 'Outbound', 'Sent', 'Delivered', 'Read', 'Failed'],
      datasets: [
        {
          label: 'Message Count',
          data: [
            stats.total,
            stats.incoming,
            stats.outbound,
            stats.sent,
            stats.delivered,
            stats.read,
            stats.failed,
          ],
          backgroundColor: [
            '#4CAF50',  // total
            '#8BC34A',  // incoming
            '#607D8B',  // outbound
            '#4CAF50',  // sent
            '#2196F3',  // delivered
            '#FFC107',  // read
            '#F44336',  // failed
          ],
          borderRadius: 8,
        },
      ],
    };
  })();

  return (
    <div className="dashboard-messages-block">
      <div className="dashboard-messages-block-header-row">
        <h2 className="dashboard-messages-block-title">Messages</h2>
        <button className="dashboard-messages-block-toggle" onClick={() => setShowChart(!showChart)}>
          {showChart ? 'Show Table' : 'Show Chart'}
        </button>
      </div>

      {showChart ? (
        <>
          <div className="dashboard-messages-block-controls">
            <select value={selectedTimeRange} onChange={(e) => setSelectedTimeRange(e.target.value)}>
              {timeOptions.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </select>

            <label className="dashboard-messages-block-user-toggle">
              <input
                type="checkbox"
                checked={groupByUser}
                onChange={() => {
                  setGroupByUser(!groupByUser);
                  setSelectedUser('');
                }}
              />
              Group by user
            </label>

            <select
              value={selectedUser}
              onChange={(e) => {
                setSelectedUser(e.target.value);
                setGroupByUser(false);
              }}
            >
              <option value="">Filter by user</option>
              {users.map((user) => (
                <option key={user.uID} value={user.UserName}>
                  {user.UserName}
                </option>
              ))}
            </select>
          </div>

          <div className="dashboard-messages-block-chart">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: true },
                },
              }}
            />
          </div>
        </>
      ) : (
        <div className="dashboard-messages-block-grid">
          <div className="dashboard-messages-block-label"></div>
          <div className="dashboard-messages-block-header">Total</div>
          <div className="dashboard-messages-block-header">Incoming</div>
          <div className="dashboard-messages-block-header">Outbound</div>

          <div className="dashboard-messages-block-label">Count</div>
          <div className="dashboard-messages-block-cell">{total}</div>
          <div className="dashboard-messages-block-cell">{incoming}</div>
          <div className="dashboard-messages-block-cell">{outbound}</div>

          <div className="dashboard-messages-block-label">Sent</div>
          <div className="dashboard-messages-block-cell">{sent}</div>
          <div className="dashboard-messages-block-cell"></div>
          <div className="dashboard-messages-block-cell"></div>

          <div className="dashboard-messages-block-label">Delivered</div>
          <div className="dashboard-messages-block-cell dashboard-messages-delivered-cell">{delivered}</div>
          <div className="dashboard-messages-block-cell"></div>
          <div className="dashboard-messages-block-cell"></div>

          <div className="dashboard-messages-block-label">Read</div>
          <div className="dashboard-messages-block-cell">{read}</div>
          <div className="dashboard-messages-block-cell"></div>
          <div className="dashboard-messages-block-cell"></div>

          <div className="dashboard-messages-block-label">Failed</div>
          <div className="dashboard-messages-block-cell dashboard-messages-failed-cell">{failed}</div>
          <div className="dashboard-messages-block-cell"></div>
          <div className="dashboard-messages-block-cell"></div>
        </div>
      )}
    </div>
  );
};

export default DashboardMessagesBlock;
